import React, { useState } from 'react';
import DraggableSliderContainer from '@Client/runner.directives/common/DraggableSlider/DraggableSliderContainer';
import {
  IEditorProcedureActionDetail,
  IImageDimensions
} from './ProcedureActionEditor';

type Props = {
  action: IEditorProcedureActionDetail;
  index: number;
  onClose: () => void;
  onNavigate: (newIndex: number) => void;
};

const ProcedureActionImageView = ({
  action,
  index,
  onClose,
  onNavigate
}: Props) => {
  const [imageZoom, setImageZoom] = useState(100);
  const [imageDimensions, setImageDimensions] =
    useState<IImageDimensions | null>(null);
  const imageRef = React.useRef<HTMLImageElement | null>(null);
  const minZoom = 100;
  const maxZoom = 200;

  const focusImageOnActionPoint = (
    img: HTMLImageElement,
    dimensions: IImageDimensions | null
  ) => {
    if (!img || !dimensions) return;

    const parent = img.parentElement;
    if (imageZoom === 100) {
      parent.scrollLeft = 0;
      parent.scrollTop = 0;
      parent.style.overflow = 'hidden';
      return;
    }
    parent.style.overflow = 'auto';
    const mouseXPercent = action.mouseX / dimensions.naturalWidth;
    const mouseYPercent = action.mouseY / dimensions.naturalHeight;

    const zoomedX = img.clientWidth * mouseXPercent;
    const zoomedY = img.clientHeight * mouseYPercent;

    const scrollLeft = zoomedX - parent.clientWidth / 2;
    const scrollTop = zoomedY - parent.clientHeight / 2;

    const cappedScrollLeft = Math.min(
      scrollLeft,
      img.offsetWidth - parent.clientWidth
    );
    const cappedScrollTop = Math.min(
      scrollTop,
      img.offsetHeight - parent.clientHeight
    );
    parent.scrollLeft = Math.max(0, cappedScrollLeft);
    parent.scrollTop = Math.max(0, cappedScrollTop);
  };
  React.useEffect(() => {
    focusImageOnActionPoint(imageRef.current, imageDimensions);
  }, [imageRef.current, imageZoom, imageDimensions]);

  const updateZoomFromWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    event.preventDefault();
    const delta = (maxZoom - minZoom) / 10;
    const zoomDelta = event.deltaY < 0 ? delta : -delta;
    const newZoom = Math.min(Math.max(imageZoom + zoomDelta, minZoom), maxZoom);
    setImageZoom(newZoom);
  };

  return (
    action && (
      <div className="procedure-image-view">
        <div
          className="navigator navigate-previous"
          title="Previous"
          onClick={() => onNavigate(index - 1)}
        >
          <i className="fa-light fa-chevron-left"></i>
        </div>
        <div className="procedure-image" onClick={onClose}>
          <div className="header">
            <div className="procedure-field">
              <div className="field-index">
                {index < 10 ? '0' : ''}
                {index}
              </div>
              <div className="field-title">{action.title}</div>
            </div>
            <div className="fa-light fa-close" title="Close"></div>
          </div>
          <div
            className="image-container"
            onWheel={updateZoomFromWheel}
            style={{
              height: imageDimensions
                ? `${imageDimensions.height + 25}px`
                : 'unset'
            }}
          >
            <img
              ref={imageRef}
              src={action.file}
              style={{
                width: imageDimensions
                  ? `${(imageZoom / 100) * imageDimensions.width}px`
                  : '100%',
                height: imageDimensions
                  ? `${(imageZoom / 100) * imageDimensions.height}px`
                  : 'unset',
                maxHeight: imageDimensions ? 'unset' : '100%',
                maxWidth: imageDimensions ? 'unset' : '100%'
              }}
              onLoad={(event) => {
                const target = event.target as HTMLImageElement;
                setImageDimensions({
                  width: target.width,
                  height: target.height,
                  naturalWidth: target.naturalWidth,
                  naturalHeight: target.naturalHeight
                });
              }}
            ></img>
          </div>
          <div className="zoom-slider" onClick={(e) => e.stopPropagation()}>
            <DraggableSliderContainer
              min={minZoom}
              max={maxZoom}
              value={imageZoom}
              onChange={setImageZoom}
            />
          </div>
        </div>
        <div
          className="navigator navigate-next"
          title="Next"
          onClick={() => onNavigate(index + 1)}
        >
          <i className="fa-light fa-chevron-right"></i>
        </div>
      </div>
    )
  );
};

export default ProcedureActionImageView;
